import { FC, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Globe, Package, BookOpen, Target, Tag, Brain, ChevronRight, Plus, Settings, User, Lock, Cpu,
  RefreshCw, Puzzle, Database, Palette, Crown, HardDrive, Menu, Home, List, BarChart2, Calendar,
  MessageCircle, CheckCircle, Rss, Upload, Share2, Search, Route, Link2, BarChart3, GitMerge, Clock,
  Fingerprint, AlertTriangle, Scale, ClipboardCheck, GitBranch, Gamepad2, Shuffle, Zap, Combine,
  HelpCircle, Network, PieChart, Timer, Wallet, Shield, Newspaper, TrendingUp, SquareStack, Filter,
  Pencil, BrainCircuit, FlaskConical, HeartPulse, Dumbbell, Activity, ShieldAlert, MapPin, MessageSquare,
  XCircle, Map, GitCommit, Users, KeyRound, Sparkles, Trash2, type LucideIcon } from 'lucide-react';
import { useBrain } from '@/hooks/useBrain';
import { useAuth } from '@/hooks/useAuth';
import { getToken } from '@/api/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useSubscription } from '@/hooks/useSubscription';
import { MEMBERSHIP_UI_VISIBLE } from '@/config/membership';
import { useTenants } from '@/hooks/useTenants';
import SidebarFolderTree from '@/components/navigation/SidebarFolderTree';
import WorkspaceSwitcher from '@/components/navigation/WorkspaceSwitcher';
import { roleLabel } from '@/utils/tenant';
import {
  useMenuData,
  useNavigation,
  SETTINGS_ITEMS,
  getVisibleItems,
  type BrainSide,
} from '@/store/navigation';

const ICON_MAP: Record<string, LucideIcon> = {
  FileText, Globe, Package, BookOpen, Target, Tag, Brain, Settings, User, Lock, Cpu,
  RefreshCw, Puzzle, Database, Palette, HardDrive, Crown, Menu, Home, List, BarChart2, Calendar,
  MessageCircle, CheckCircle, Rss, Upload, Share2, Search, Route, Link2, BarChart3, GitMerge, Clock,
  Fingerprint, AlertTriangle, Scale, ClipboardCheck, GitBranch, Gamepad2, Shuffle, Zap, Combine,
  HelpCircle, Network, PieChart, Timer, Wallet, Shield, Newspaper, TrendingUp, SquareStack, Filter,
  Pencil, BrainCircuit, FlaskConical, HeartPulse, Dumbbell, Activity, ShieldAlert, MapPin, MessageSquare,
  XCircle, Map, GitCommit, KeyRound, Sparkles, Trash2,
};

const Sidebar: FC = () => {
  const { activeBrain, switchBrain, isSwitching } = useBrain();
  const { isLoggedIn } = useAuth();
  const { brainSide: navBrainSide, setBrainSide } = useNavigation();
  const queryClient = useQueryClient();
  const { tier, isLoading: subLoading } = useSubscription();
  // 团队空间=第三作用域：激活时脑切换器（个人专属）隐藏；文件夹树两空间都显示（服务端按空间返回团队夹）
  // 游客演示也拉取（GET 由游客中间件注入演示号），用于侧边栏团队 badge 与切换器展示
  const { activeTenant } = useTenants();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [brainMenuOpen, setBrainMenuOpen] = useState(false);

  // 显示口径统一走本地导航 store：登录态由 useBrain 把服务端 active_brain 同步进来；
  // 游客是纯前端切换，服务端值不回顶（否则切换器显示与实际状态分裂）
  const effectiveBrain = navBrainSide || activeBrain || 'both';
  const { quickActions: modeQuickActions } = useMenuData();
  const quickActions = getVisibleItems(modeQuickActions, effectiveBrain);

  const handleBrainSwitch = async (target: BrainSide) => {
    // 游客演示（无本地 token）：脑侧是纯前端状态（列表 GET 带 brain_side 参数），
    // 本地切换即生效；服务端 switch 接口要 POST，游客走不通
    if (!getToken()) {
      setBrainSide(target);
      queryClient.invalidateQueries();
      setBrainMenuOpen(false);
      return;
    }
    try {
      await switchBrain(target);
    } catch (err: any) {
      console.error('Brain switch failed:', err);
      // 不再静默吞 401——任何失败都给出反馈（血泪：静默吞异常让缺陷隐形）
      alert('切换失败: ' + (err?.response?.data?.message || err?.message || '请稍后再试'));
    }
    setBrainMenuOpen(false);
  };

  const brainOptions = [
    { id: 'personal', label: '个人脑', sublabel: '我的思考与沉淀', icon: Home, color: 'text-personal-primary', bg: 'bg-personal-primary/10', border: 'border-personal-primary/25' },
    { id: 'network', label: '网络脑', sublabel: '外部采集的知识', icon: Globe, color: 'text-network-primary', bg: 'bg-network-primary/10', border: 'border-network-primary/25' },
    { id: 'both', label: '整合脑', sublabel: '两侧融合检索', icon: Brain, color: 'text-fusion-primary', bg: 'bg-fusion-primary/10', border: 'border-fusion-primary/25' },
  ];

  const brainColor = effectiveBrain === 'personal' ? 'text-personal-primary' :
                     effectiveBrain === 'network' ? 'text-network-primary' : 'text-fusion-primary';
  const brainBg = effectiveBrain === 'personal' ? 'bg-personal-primary/10' :
                  effectiveBrain === 'network' ? 'bg-network-primary/10' : 'bg-fusion-primary/10';
  const BrainIcon = effectiveBrain === 'personal' ? Home :
                    effectiveBrain === 'network' ? Globe : Brain;

  return (
    <div className="h-full flex flex-col p-3 min-h-0">
      {/* 工作区切换器（个人空间 ⇄ 团队空间，最顶部；游客不渲染） */}
      <WorkspaceSwitcher />

      {/* 团队空间激活时的常驻提示 badge（脑切换器是个人专属，隐藏） */}
      {activeTenant && (
        <div className="mb-4 px-3 py-2 rounded-[2px] border border-info/30 bg-info/10 flex items-center gap-2">
          <Users className="w-3.5 h-3.5 text-info shrink-0" />
          <span className="text-xs text-info truncate">
            团队空间 · {activeTenant.name} · {roleLabel(activeTenant.my_role)} · {activeTenant.member_count}/{activeTenant.max_users} 席
          </span>
        </div>
      )}

      {!activeTenant && (
        <>
        {/* Brain Switcher */}
        <div className="relative mb-4">
          <button
            onClick={() => setBrainMenuOpen(!brainMenuOpen)}
            disabled={isSwitching}
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-[2px] border border-border-color transition-colors duration-200 ${brainBg} hover:border-current/30 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isSwitching ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <BrainIcon className={`w-5 h-5 ${brainColor}`} />
            )}
            <div className="flex-1 text-left">
              <div className={`text-xs font-semibold ${brainColor}`}>
                {effectiveBrain === 'personal' ? '个人脑' :
                 effectiveBrain === 'network' ? '网络脑' : '整合脑'}
              </div>
              <div className="text-[10px] text-text-muted">
                {effectiveBrain === 'personal' ? '我的思考与沉淀' :
                 effectiveBrain === 'network' ? '外部采集的知识' : '两侧融合检索'}
              </div>
            </div>
            <div className={`w-2 h-2 rounded-full ${brainColor.replace('text-', 'bg-')} animate-pulse`} />
            <motion.svg
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              className="text-text-muted"
              animate={{ rotate: brainMenuOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <polyline points="6 9 12 15 18 9" />
            </motion.svg>
          </button>

          <AnimatePresence>
            {brainMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full left-0 right-0 mt-2 glass rounded-[2px] border border-border-color p-2 z-50"
              >
                {brainOptions.map((opt) => {
                  const Icon = opt.icon;
                  const isActive = effectiveBrain === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => handleBrainSwitch(opt.id as BrainSide)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-[2px] text-sm transition-colors ${
                        isActive
                          ? `${opt.bg} ${opt.color} border ${opt.border}`
                          : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-[2px] ${opt.bg} flex items-center justify-center`}>
                        <Icon className={`w-4 h-4 ${opt.color}`} />
                      </div>
                      <div className="flex-1 text-left">
                        <div className={`text-sm font-medium ${isActive ? opt.color : 'text-text-primary'}`}>
                          {opt.label}
                        </div>
                        <div className="text-[10px] text-text-muted">{opt.sublabel}</div>
                      </div>
                      {isActive && (
                        <div className={`w-2 h-2 rounded-full ${opt.color.replace('text-', 'bg-')}`} />
                      )}
                    </button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        </>
      )}

      {/* 文件夹树（游客演示只读展示；点节点跳 /folders/:id。
          团队空间照常显示——服务端按空间口径返回团队夹，脑侧分组双列展示） */}
      <SidebarFolderTree brainSide={activeTenant ? 'both' : effectiveBrain} readOnly={!isLoggedIn} />

      {/* Quick Actions */}
      <div className="mb-4">
        <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2 px-1">
          快速操作
        </div>
        <div className="space-y-1">
          {quickActions.map((action) => {
            const Icon = ICON_MAP[action.icon] || Brain;
            return (
              <button
                key={action.id}
                onClick={() => navigate(action.path)}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-[2px] text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors group"
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1 text-left">{action.label}</span>
                <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Recent Items - placeholder until API ready */}
      <div className="flex-1 overflow-hidden min-h-0">
        <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2 px-1">
          最近内容
        </div>
        <div className="flex items-center justify-center h-24 text-xs text-text-muted rounded-[2px] border border-dashed border-border-color">
          暂无最近内容
        </div>
      </div>

      {/* Bottom Stats + Menu */}
      <div className="flex-shrink-0 border-t border-border-color pt-3 mt-2 relative">
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-[2px] text-sm font-medium transition-colors duration-200 ${
              menuOpen
                ? 'bg-accent/10 text-accent border border-accent/25'
                : 'hover:bg-bg-hover text-text-secondary hover:text-text-primary border border-transparent'
            }`}
          >
            <Menu className="w-4 h-4" />
            <span className="flex-1 text-left">菜单</span>
            <motion.svg
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              animate={{ rotate: menuOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <polyline points="6 9 12 15 18 9" />
            </motion.svg>
          </button>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="absolute bottom-full left-0 right-0 mb-2 glass-popup rounded-[2px] border border-border-color p-2 z-50"
              >
                {/* Settings Option */}
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[2px] text-sm transition-colors ${
                    settingsOpen
                      ? 'bg-accent/10 text-accent'
                      : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                  }`}
                >
                  <div className="w-7 h-7 rounded-[2px] bg-accent/10 flex items-center justify-center text-accent">
                    <Settings className="w-3.5 h-3.5" />
                  </div>
                  <span className="flex-1 text-left">设置</span>
                  <motion.svg
                    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    animate={{ rotate: settingsOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </motion.svg>
                </button>

                <AnimatePresence>
                  {settingsOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-2 mt-1 space-y-0.5 border-l-2 border-border-color ml-4">
                        {SETTINGS_ITEMS.map((item) => {
                          const Icon = ICON_MAP[item.icon] || Settings;
                          return (
                            <button
                              key={item.id}
                              onClick={() => {
                                navigate(item.path);
                                setSettingsOpen(false);
                                setMenuOpen(false);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-[2px] text-xs text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
                            >
                              <Icon className="w-3 h-3 text-text-muted" />
                              <span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {MEMBERSHIP_UI_VISIBLE && (
                  <>
                    <div className="my-1.5 border-t border-border-color" />
                    <button
                      onClick={() => {
                        navigate('/payment');
                        setMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-[2px] text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
                    >
                      <div className={`w-7 h-7 rounded-[2px] flex items-center justify-center ${
                        tier === 'storage' ? 'bg-warning/10 text-warning' : 'bg-fusion-primary/10 text-fusion-primary'
                      }`}>
                        <Crown className="w-3.5 h-3.5" />
                      </div>
                      <span className="flex-1 text-left">
                        {subLoading ? '加载中...' : tier === 'free' ? '免费版' : 'Pro 会员'}
                      </span>
                      <ChevronRight className="w-3 h-3 text-text-muted" />
                    </button>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
